'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getHeroHeaderSizeClasses } from '@/utils/getHeroHeaderSizeClasses';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getMainHeroVerticalSpacing } from '@/utils/getVerticalSpacing';
import { isVideoUrl } from '@/utils/isVideoUrl';
import { normalizeSvgEmbedMarkup } from '@/utils/normalizeSvgEmbed';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';
import VideoEmbed from './atoms/VideoEmbed';

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && x && 'value' in x) return x.value ?? fallback;
    return x;
}

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade-in': 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide-up': 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom-in': 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (['media', 'medias', 'image', 'images'].includes(v)) return 'medias';
    if (['cta', 'ctas', 'buttons', 'button'].includes(v)) return 'ctas';
    return 'content';
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 20 },
            onscreen: { opacity: 1, y: 0, transition: { duration: d } },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 1.05 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { duration: d },
            },
        };
    }
    if (preset === 'stagger') {
        return {
            offscreen: { opacity: 0, y: 12 },
            onscreen: { opacity: 1, y: 0, transition: { duration: d } },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { duration: d } },
    };
}

function getStaggerContainer(duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
}

function MotionMaybe({
    enable,
    k,
    motionProps,
    className,
    style = {},
    children,
}) {
    if (enable) {
        return (
            <motion.div
                key={`${k}-on`}
                className={className}
                style={style}
                {...motionProps}
            >
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '', ...style }}
        >
            {children}
        </div>
    );
}

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

export default function HeroHalf(props) {
    const normalizeEnterArtifacts = (text) => {
        if (typeof text !== 'string') return text;
        // Some editors insert "****" on Enter; treat it as a line break.
        return text.replace(/\s*\*{4}\s*/g, '\n');
    };

    const {
        Header,
        SubHeader,
        HeaderSize = 'large',
        Body,
        ImageUrl,
        Video,
        AltText,
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        ImagePosition = 'right',
        ImageObjectPosition = '50',
        Animation = 'fade',
        AnimationGroup = 'content',
        Duration = 0.6,
        EditorPreview = false,
    } = props;

    const {
        backgroundColor,
        headerColor,
        textColor,
        thirdButton,
        secondaryButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    // Centralized vertical spacing (main hero presets with pb-0 for no-bottom variants)
    const verticalRaw =
        VerticalSpacing &&
        typeof VerticalSpacing === 'object' &&
        'value' in VerticalSpacing
            ? VerticalSpacing.value
            : VerticalSpacing;
    const verticalKey = String(verticalRaw || 'medium').toLowerCase();
    const heroSpace = getMainHeroVerticalSpacing(verticalKey);
    const vSpace = `${heroSpace.className}`;

    const safeHeader = normalizeEnterArtifacts(Header);
    const safeSubHeader = normalizeEnterArtifacts(SubHeader);
    const headerSizeClass = getHeroHeaderSizeClasses(HeaderSize, 'page');
    const subHeaderSizeClass = getHeroHeaderSizeClasses('large', 'page');

    const normalizedImageUrl = valOf(ImageUrl, '');
    const normalizedVideoUrl = valOf(Video, '');
    const videoIsEmbed = isEmbedCode(normalizedVideoUrl);
    const imageIsEmbed = isEmbedCode(normalizedImageUrl);
    const fallbackVideoUrl =
        !normalizedVideoUrl && isVideoUrl(normalizedImageUrl)
            ? normalizedImageUrl
            : '';
    const videoUrl = videoIsEmbed ? '' : normalizedVideoUrl || fallbackVideoUrl;
    const embedHtml = videoIsEmbed
        ? normalizedVideoUrl
        : imageIsEmbed
        ? normalizedImageUrl
        : '';
    const imageUrl = videoUrl || embedHtml ? '' : normalizedImageUrl;

    const imagePositionRaw = valOf(
        ImagePosition,
        valOf(props.ImageSide, 'right')
    );
    const imagePosition = String(imagePositionRaw || 'right').toLowerCase();
    const imageLeft = imagePosition === 'left';
    const textOrderClass = imageLeft ? 'lg:order-2' : 'lg:order-1';
    const mediaOrderClass = imageLeft ? 'lg:order-1' : 'lg:order-2';
    const { x: imagePosX, y: imagePosY } =
        parseImagePosition(ImageObjectPosition);
    const imageObjectPosition = `${imagePosX}% ${imagePosY}%`;
    const normalizedEmbedHtml = embedHtml
        ? normalizeSvgEmbedMarkup(embedHtml, {
              x: imagePosX,
              y: imagePosY,
              mode: 'cover',
          })
        : '';

    const preset = normalizeAnimation(valOf(Animation, 'fade'));
    const duration = Number(valOf(Duration, 0.6)) || 0.6;
    const group = mapGroup(valOf(AnimationGroup, 'content'));
    const animateText = group === 'content';
    const animateMedia = group === 'medias';
    const animateCTAs = group === 'ctas';

    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = (e) => setPrefersReducedMotion(e.matches);
        setPrefersReducedMotion(mq.matches);
        mq.addEventListener?.('change', update);
        return () => mq.removeEventListener?.('change', update);
    }, []);

    const enableAnim = !prefersReducedMotion && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.4 };
    const variants = getElementVariants(preset, duration);
    const baseKey = `herohalf-${group}-${preset}-${duration}-${
        EditorPreview ? 'preview' : 'live'
    }`;

    const buildMotionProps = (v) => {
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    };

    const textContainerMotionProps =
        enableAnim && animateText && preset === 'stagger'
            ? buildMotionProps(getStaggerContainer(duration))
            : {};

    const textChildMotionProps =
        enableAnim && animateText
            ? preset === 'stagger'
                ? buildMotionProps({
                      hidden: { opacity: 0, y: 16 },
                      show: {
                          opacity: 1,
                          y: 0,
                          transition: {
                              duration: Math.max(0.4, duration - 0.2 || 0.2),
                          },
                      },
                  })
                : buildMotionProps(variants)
            : {};

    const mediaMotionProps =
        enableAnim && animateMedia ? buildMotionProps(variants) : {};
    const ctaMotionProps =
        enableAnim && animateCTAs ? buildMotionProps(variants) : {};

    return (
        <div
            id="top"
            className={`editable-component relative ${backgroundColor}`}
            style={{
                ...backgroundStyle,
                marginTop: '-172px',
                '@media (minWidth: 768px)': { marginTop: '-70px' },
            }}
            data-json="herohalf"
        >
            <div className="grid grid-cols-1 lg:grid-cols-2">
                <MotionMaybe
                    enable={enableAnim && animateText && preset === 'stagger'}
                    k={`${baseKey}-text`}
                    motionProps={textContainerMotionProps}
                    className={`relative z-40 mx-auto flex-col px-6 md:max-w-3xl md:px-12 lg:flex lg:justify-center lg:px-16 xl:max-w-4xl ${vSpace} ${textOrderClass}`}
                >
                    <MotionMaybe
                        enable={enableAnim && animateText}
                        k={`${baseKey}-header`}
                        motionProps={textChildMotionProps}
                    >
                        {safeHeader && (
                            <MarkDown
                                variant="h1"
                                className={`w-full pt-16 text-left font-display tracking-tight ${headerSizeClass} ${headerColor} lg:pt-18 lg:max-w-6xl`}
                            >
                                {safeHeader}
                            </MarkDown>
                        )}
                        {safeSubHeader && (
                            <MarkDown
                                variant={getChildHeadingVariant(safeHeader, 1)}
                                className={`mt-2 w-full text-left font-display tracking-tight ${subHeaderSizeClass} ${headerColor} lg:max-w-6xl`}
                            >
                                {safeSubHeader}
                            </MarkDown>
                        )}
                    </MotionMaybe>

                    <MotionMaybe
                        enable={enableAnim && animateText}
                        k={`${baseKey}-body`}
                        motionProps={textChildMotionProps}
                    >
                        <MarkDown
                            className={`mt-6 w-full text-left text-lg font-text leading-7 md:text-xl md:leading-8 ${textColor}`}
                        >
                            {Body}
                        </MarkDown>
                    </MotionMaybe>

                    {(() => {
                        const { hasAny, element } = getCTA(props, {
                            secondaryVariant: secondaryButton,
                            outlineVariant: thirdButton,
                            buttonAlign: 'justify-start',
                            wrapSpacing: 'mt-10 lg:mt-16',
                            buttonsClassName:
                                'flex flex-wrap items-center gap-2',
                            size: 'md',
                            enableAnim: enableAnim && animateCTAs,
                            motionProps: ctaMotionProps,
                        });
                        return hasAny ? element : null;
                    })()}
                </MotionMaybe>

                <MotionMaybe
                    enable={enableAnim && animateMedia}
                    k={`${baseKey}-media`}
                    motionProps={mediaMotionProps}
                    className={`relative h-full min-h-[450px] w-full md:min-h-[550px] lg:min-h-[650px] ${mediaOrderClass}`}
                    style={{ minHeight: '450px' }}
                >
                    {embedHtml ? (
                        <div className="relative h-full w-full">
                            <div
                                className="absolute inset-0 [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block"
                                dangerouslySetInnerHTML={{
                                    __html: normalizedEmbedHtml,
                                }}
                            />
                        </div>
                    ) : videoUrl ? (
                        <div className="relative h-full w-full">
                            <VideoEmbed
                                url={videoUrl}
                                playing
                                muted
                                loop
                                controls={false}
                                width="100%"
                                height="100%"
                                className="absolute left-0 top-0 h-full w-full"
                                playsinline
                            />
                        </div>
                    ) : (
                        <img
                            className="h-full w-full object-cover"
                            style={{
                                minHeight: 'inherit',
                                objectPosition: imageObjectPosition,
                            }}
                            src={imageUrl}
                            alt={AltText}
                            width={1000}
                            height={800}
                        />
                    )}
                </MotionMaybe>
            </div>
        </div>
    );
}
